import Cookies from 'js-cookie'

import request from '@utils/request'
import { resetRouter, generateUserRoutes } from '@router'

export const state = {
  currentUser: getCookie('auth.currentUser'),
  currentUserRoutes: getCookie('auth.currentUserRoutes') || [],
  currentUserPermission: getCookie('auth.currentUserPermission') || {},
  currentUserTours: getSavedState('auth.currentUserTours'),
  currentUserAccount: getCookie('auth.currentUserAccount') || {},
  currentSubscription: getCookie('auth.currentSubscription') || null,
  currentSubscriptionAuthority:
    getCookie('auth.currentSubscriptionAuthority') || false,
}

export const mutations = {
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = newValue
    setCookie('auth.currentUser', newValue)
    setDefaultAuthHeaders(state)
  },
  SET_CURRENT_USER_ACCESS(state, { assets, ...permission }) {
    state.currentUserRoutes = assets
    state.currentUserPermission = permission
    setCookie('auth.currentUserRoutes', assets)
    setCookie('auth.currentUserPermission', permission)
  },
  SET_CURRENT_USER_TOUR(state, tourPages) {
    state.currentUserTours = tourPages
    saveState('auth.currentUserTours', tourPages)
  },
  SET_CURRENT_USER_ACCOUNT(state, account) {
    state.currentUserAccount = account
    setCookie('auth.currentUserAccount', account)
  },
  SET_CURRENT_SUBSCRIPTION(state, newValue) {
    state.currentSubscription = newValue
    setCookie('auth.currentSubscription', newValue)
  },
  SET_CURRENT_SUBSCRIPTION_AUTHORITY(state, newValue) {
    state.currentSubscriptionAuthority = newValue
    setCookie('auth.currentSubscriptionAuthority', newValue)
  },
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser && state.currentUserRoutes.length > 0
  },
  merchantUrl() {
    // IE11
    if (!location.origin) {
      location.origin = location.protocol + "//" 
        + location.hostname 
        + (location.port ? ':' + location.port : '');
    }
    const bookingOrigin = location.origin.replace('admin.', '')
    return `${bookingOrigin}/${state.currentUser &&
      state.currentUser.merchantAlias}`
  },
}

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    setDefaultAuthHeaders(state)
    // dispatch('validate')
  },
  fetchTour({ commit }, readPage) {
    return request
      .get('/admin/merchant/user/tour', {
        params: {
          read: readPage,
        },
      })
      .then((response) => {
        const tourPages = response.items
        commit('SET_CURRENT_USER_TOUR', tourPages)

        return tourPages
      })
  },
  fetchAccess({ commit }) {
    return request.get('/admin/merchant/user/access').then((response) => {
      commit('SET_CURRENT_USER_ACCESS', response.items)

      return generateUserRoutes(state)
    })
  },
  reset({ commit }, resetData) {
    // token, password, passwordConfirm
    return request.post('/password/reset', resetData)
  },
  forgot({ commit }, email) {
    return request.post('/password/email', {
      email,
    })
  },
  // Logs in the current user.
  logIn({ commit, dispatch, getters }, { email, password } = {}) {
    // if (getters.loggedIn) return dispatch('validate')

    return request
      .post(
        '/merchant/user/login',
        {
          email,
          password,
        },
        {
          errorHandle: false,
          hideErrorMessage: true,
        }
      )
      .then((response) => {
        console.log('response', response)
        const user = response.items
        commit('SET_CURRENT_USER', user)
        commit('SET_CURRENT_USER_ACCOUNT', {
          email,
          password,
        })
        return user
      })
      .catch((error) => {
        if (
          error.status === 'MERCHANT_FRONT_PLEASE_RENEW_SUBSCRIPTION' ||
          error.status === 'MERCHANT_FRONT_PLEASE_BIND_CARD_FIRST'
        ) {
          commit('SET_CURRENT_USER', error.data.items)
          commit('SET_CURRENT_SUBSCRIPTION', error.data.items)
        }
        return Promise.reject(error)
      })
  },
  /**
   * 更新方案資訊
   */
  clearSubscription({ commit, state }) {
    commit('SET_CURRENT_SUBSCRIPTION', null)
    return Promise.resolve()
  },
  // 判斷訂閱方案的藍星金流 callback 頁面
  updateAccessSubscription({ commit }, status) {
    commit('SET_CURRENT_SUBSCRIPTION_AUTHORITY', status)
    return Promise.resolve()
  },

  // Logs out the current user.
  logOut({ commit }) {
    return request.get('/merchant/user/logout').then(() => {
      commit('SET_CURRENT_USER', null)
      commit('SET_CURRENT_USER_ACCESS', {
        assets: [],
      })
      commit('SET_CURRENT_USER_TOUR', null)
      commit('SET_CURRENT_USER_ACCOUNT', null)
      resetRouter()
      return Promise.resolve()
    })
  },
  validateOrder({ commit }, orderId) {
    return validateUser(
      {
        commit,
      },
      `/merchant/${orderId}/valid`
    )
  },
  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ commit, state, dispatch }) {
    if (!state.currentUser) return Promise.resolve(null)

    return validateUser(
      {
        commit,
        dispatch,
      },
      '/merchant/user/login'
    )
  },
}

// ===
// Private helpers
// ===

function validateUser({ commit }, requestPath) {
  return request
    .get(requestPath, {
      errorHandle: false,
    })
    .then((response) => {
      const user = response.items
      commit('SET_CURRENT_USER', user)

      return user
    })
    .catch((error) => {
      if (error && error.code === 401) {
        commit('SET_CURRENT_USER', null)
      }
      return null
    })
}

function getCookie(key) {
  return JSON.parse(Cookies.get(key) || null)
}

function setCookie(key, state) {
  return Cookies.set(key, JSON.stringify(state), {
    expires: 7,
    sameSite: 'lax',
  })
}

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key))
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

function setDefaultAuthHeaders(state) {
  request.defaults.headers.common.Authorization = state.currentUser
    ? `Bearer ${state.currentUser.token}`
    : ''
}
